/**
 * PROCEDURAL TEXTURE FORGE
 * ========================
 *
 * There is no source art in this project. Every surface in the world is baked
 * here, at runtime, into a 2D canvas and handed to Three as a `CanvasTexture`.
 *
 * Why bother, when a cel-shaded prop could just be flat brand colour? Because
 * flat colour is exactly what makes procedural 3D read as "programmer art".
 * The reference is a *painted storybook*: cream floors have paper tooth and
 * hand-wobbled plank seams, sofas have visible weave, tiles have grout that was
 * clearly drawn by a hand rather than a rect(). Those micro-signals are what a
 * blind A/B judge responds to even when they cannot name why.
 *
 * Design rules obeyed throughout:
 *
 *  - **Palette only.** Every default colour is `HEX.*` from `render/palette.ts`
 *    or a documented mix of two palette entries. Nothing invents a hue.
 *  - **Tileable by construction.** Seams sit on a lattice and wobble along a
 *    *periodic* noise field; low-frequency fields are upscaled from a wrapped
 *    pad; grain is a repeating pattern. `RepeatWrapping` never shows a join.
 *  - **Memoised.** Every generator hashes its resolved options and returns the
 *    cached texture. The same floor is never baked twice.
 *  - **No full-resolution pixel loops.** A 512x512 JS loop costs 100ms+ on a
 *    modest machine, and a scene that bakes eight surfaces would stall for a
 *    second. Large soft fields are painted at ~32px and upscaled by the
 *    rasteriser (free bilinear); high-frequency grain is one cached 128px tile
 *    stamped as a repeating pattern. Everything else is vector work.
 *  - **Correct colour space.** Colour maps are tagged `SRGBColorSpace`; height,
 *    mask and alpha maps are tagged `NoColorSpace` so they are not gamma-bent.
 *  - **Anisotropy + wrapping** are configured on every texture, because a floor
 *    viewed at a 42-degree dimetric tilt is the worst case for mip blur.
 *
 * Sizes should be powers of two (256 / 512 / 1024): the grain pattern tiles at
 * 128px and mipmaps want POT.
 *
 * Lifetime: the cache is global and intentionally survives scene swaps — these
 * are shared world assets. `disposeTree` in `cel.ts` disposes geometries and
 * materials but NOT textures, which is exactly right. Call
 * `disposeForgeTextures()` only when tearing down the whole app.
 */
import {
  CanvasTexture,
  ClampToEdgeWrapping,
  MirroredRepeatWrapping,
  NoColorSpace,
  RepeatWrapping,
  SRGBColorSpace,
  type Wrapping,
} from 'three';
import { HEX } from './palette';

/* ------------------------------------------------------------------ *
 * Colour maths, in sRGB hex space (the space the palette is authored in)
 * ------------------------------------------------------------------ */

export function rgbOf(hex: number): [number, number, number] {
  return [(hex >> 16) & 255, (hex >> 8) & 255, hex & 255];
}

export function hexOf(r: number, g: number, b: number): number {
  const c = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));
  return (c(r) << 16) | (c(g) << 8) | c(b);
}

/** Linear blend between two sRGB hex colours. */
export function mixHex(a: number, b: number, t: number): number {
  const [ar, ag, ab] = rgbOf(a);
  const [br, bg, bb] = rgbOf(b);
  return hexOf(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

/**
 * Lighten (`amount > 0`, toward white) or deepen (`amount < 0`, toward the
 * palette's ink). Shading toward ink rather than pure black keeps every derived
 * tone inside the brand family — the same principle `cel.ts` applies in 3D.
 */
export function shadeHex(hex: number, amount: number): number {
  return amount >= 0 ? mixHex(hex, HEX.white, amount) : mixHex(hex, HEX.ink, -amount);
}

/** `rgb()` / `rgba()` CSS string for a canvas fill or stroke. */
export function css(hex: number, alpha = 1): string {
  const [r, g, b] = rgbOf(hex);
  return alpha >= 1 ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Derived surface tones. Each is a mix of two palette entries, so the whole
 * material library provably stays inside the approved colour world.
 */
export const SURFACE = {
  floor: HEX.warmCream,
  floorDeep: mixHex(HEX.warmCream, HEX.ink, 0.14),
  floorSeam: mixHex(HEX.warmCream, HEX.ink, 0.34),
  floorLift: mixHex(HEX.warmCream, HEX.white, 0.5),

  wood: HEX.deepGreen,
  woodGrain: mixHex(HEX.deepGreen, HEX.ink, 0.4),
  woodLift: mixHex(HEX.deepGreen, HEX.softGreen, 0.3),

  fabric: HEX.softGreen,
  fabricWeft: mixHex(HEX.softGreen, HEX.white, 0.34),
  fabricWarp: mixHex(HEX.softGreen, HEX.deepGreen, 0.28),

  tile: HEX.mintPanel,
  grout: mixHex(HEX.mintPanel, HEX.ink, 0.3),

  metal: mixHex(HEX.white, HEX.deepGreen, 0.44),
  metalDark: mixHex(HEX.deepGreen, HEX.ink, 0.22),
  metalLift: mixHex(HEX.white, HEX.mintPanel, 0.35),

  plastic: HEX.white,
  ink: HEX.ink,
} as const;

/* ------------------------------------------------------------------ *
 * Deterministic randomness + tileable noise
 * ------------------------------------------------------------------ */

/** mulberry32 — small, fast, well-distributed, and fully reproducible. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

export type Noise2D = (x: number, y: number) => number;

/**
 * Value noise on a `period x period` lattice with wrap-around indexing, so the
 * field is exactly periodic over [0,1). This is the whole trick behind seamless
 * tiling: sample it with x,y in unit space and the left edge equals the right.
 *
 * Only ever called a few hundred times per texture (seam wobble, small fields),
 * never per output pixel.
 */
function tileNoise(rand: () => number, period: number): Noise2D {
  const p = Math.max(2, Math.round(period));
  const v = new Float32Array(p * p);
  for (let i = 0; i < v.length; i++) v[i] = rand();

  return (x, y) => {
    const fx = x * p;
    const fy = y * p;
    const x0 = Math.floor(fx);
    const y0 = Math.floor(fy);
    const tx = fx - x0;
    const ty = fy - y0;
    const sx = tx * tx * (3 - 2 * tx);
    const sy = ty * ty * (3 - 2 * ty);
    const i0 = ((x0 % p) + p) % p;
    const i1 = (i0 + 1) % p;
    const j0 = ((y0 % p) + p) % p;
    const j1 = (j0 + 1) % p;
    const a = v[j0 * p + i0]!;
    const b = v[j0 * p + i1]!;
    const c = v[j1 * p + i0]!;
    const d = v[j1 * p + i1]!;
    return (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy;
  };
}

/** Fractal sum of periodic value noise. Still exactly tileable. */
function tileFbm(rand: () => number, basePeriod: number, octaves: number): Noise2D {
  const layers: Array<{ n: Noise2D; amp: number }> = [];
  let amp = 1;
  let total = 0;
  for (let o = 0; o < octaves; o++) {
    layers.push({ n: tileNoise(rand, basePeriod * Math.pow(2, o)), amp });
    total += amp;
    amp *= 0.5;
  }
  return (x, y) => {
    let s = 0;
    for (const l of layers) s += l.n(x, y) * l.amp;
    return s / total;
  };
}

/* ------------------------------------------------------------------ *
 * Canvas helpers — the "drawn by hand" vocabulary
 * ------------------------------------------------------------------ */

function make2d(w: number, h: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('textures.ts: 2D canvas context unavailable');
  return { canvas, ctx };
}

/** Rounded rectangle path. Hand-rolled so we do not depend on `ctx.roundRect`. */
function rrect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  const rr = Math.max(0, Math.min(r, Math.min(w, h) / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/**
 * Draw a small mark, repeating it across the tile border when it is close
 * enough to spill. One to four draws instead of a blanket 3x3.
 */
function wrapMark(
  size: number,
  x: number,
  y: number,
  reach: number,
  draw: (cx: number, cy: number) => void
): void {
  draw(x, y);
  const ox = x < reach ? size : x > size - reach ? -size : 0;
  const oy = y < reach ? size : y > size - reach ? -size : 0;
  if (ox) draw(x + ox, y);
  if (oy) draw(x, y + oy);
  if (ox && oy) draw(x + ox, y + oy);
}

interface SeamStyle {
  color: number;
  alpha: number;
  width: number;
  /** Peak deviation in pixels. This is what stops a seam looking like a rect(). */
  amp: number;
  /** Which row of the noise field to walk, so parallel seams differ. */
  lane: number;
  steps?: number;
}

/**
 * A horizontal seam that wobbles. The offset comes from the *periodic* noise
 * field, so the wobble at x=0 equals the wobble at x=size and the tile joins.
 */
function seamH(
  ctx: CanvasRenderingContext2D,
  size: number,
  y: number,
  n: Noise2D,
  s: SeamStyle
): void {
  const steps = s.steps ?? 48;
  ctx.beginPath();
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const px = t * size;
    const py = y + (n(t, s.lane) - 0.5) * 2 * s.amp;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.strokeStyle = css(s.color, s.alpha);
  ctx.lineWidth = s.width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();
}

/** Vertical counterpart of {@link seamH}. */
function seamV(
  ctx: CanvasRenderingContext2D,
  size: number,
  x: number,
  y0: number,
  y1: number,
  n: Noise2D,
  s: SeamStyle
): void {
  const steps = s.steps ?? 24;
  ctx.beginPath();
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const py = y0 + (y1 - y0) * t;
    const px = x + (n(s.lane, py / size) - 0.5) * 2 * s.amp;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.strokeStyle = css(s.color, s.alpha);
  ctx.lineWidth = s.width;
  ctx.lineCap = 'round';
  ctx.stroke();
}

/* ---------------- low-frequency painted fields --------------------- */

/**
 * Paint a soft, large-scale colour field without touching a single output
 * pixel from JS.
 *
 * The field is authored at `cells x cells` (typically 16-40 px), padded by one
 * cell of wrapped data on every side, then blitted across the tile at a
 * destination rect one cell oversized. The rasteriser's own bilinear filter
 * does the interpolation, and the padding makes the left and right edges
 * resolve to the identical blend — so the upscale is still perfectly tileable.
 *
 * `value(i, j)` returns a signed -1..1 amount: positive picks `posColor`,
 * negative picks `negColor`, magnitude drives opacity.
 */
function paintField(
  ctx: CanvasRenderingContext2D,
  size: number,
  cells: number,
  posColor: number,
  negColor: number,
  strength: number,
  value: (i: number, j: number) => number
): void {
  const N = Math.max(3, Math.round(cells));
  const M = N + 2;
  const { canvas, ctx: c2 } = make2d(M, M);
  const img = c2.createImageData(M, M);
  const d = img.data;
  const [pr, pg, pb] = rgbOf(posColor);
  const [nr, ng, nb] = rgbOf(negColor);

  for (let j = 0; j < M; j++) {
    const sj = (((j - 1) % N) + N) % N;
    for (let i = 0; i < M; i++) {
      const si = (((i - 1) % N) + N) % N;
      const v = value(si, sj);
      const o = (j * M + i) * 4;
      if (v >= 0) {
        d[o] = pr;
        d[o + 1] = pg;
        d[o + 2] = pb;
      } else {
        d[o] = nr;
        d[o + 1] = ng;
        d[o + 2] = nb;
      }
      d[o + 3] = Math.max(0, Math.min(255, Math.abs(v) * strength * 255));
    }
  }
  c2.putImageData(img, 0, 0);

  const cell = size / N;
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(canvas, -cell, -cell, size + cell * 2, size + cell * 2);
  ctx.restore();
}

/* ---------------- high-frequency grain ----------------------------- */

const grainTiles = new Map<string, HTMLCanvasElement>();
const GRAIN_TILE = 128;

/**
 * One 128px tile of neutral-centred noise, stamped as a repeating pattern in
 * `overlay` blend mode. Overlay is the key: 50% grey is the identity for that
 * blend, so the tile brightens and darkens without shifting the average tone.
 *
 * Built once per (strength, warmth, seed) and shared by every texture that asks
 * for the same tooth — grain is the most-used pass in the library and the one
 * that would otherwise dominate bake time.
 */
function grainTile(strength: number, warm: number, seed: number): HTMLCanvasElement {
  const key = `${strength.toFixed(3)}|${warm}|${seed}`;
  const hit = grainTiles.get(key);
  if (hit) return hit;

  const { canvas, ctx } = make2d(GRAIN_TILE, GRAIN_TILE);
  const img = ctx.createImageData(GRAIN_TILE, GRAIN_TILE);
  const d = img.data;
  const rand = mulberry32((seed >>> 0) || 1);
  const amp = strength * 255;
  for (let i = 0; i < GRAIN_TILE * GRAIN_TILE; i++) {
    const v = 128 + (rand() - 0.5) * 2 * amp;
    const o = i * 4;
    d[o] = v + warm;
    d[o + 1] = v;
    d[o + 2] = v - warm;
    d[o + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  grainTiles.set(key, canvas);
  return canvas;
}

function applyGrain(
  ctx: CanvasRenderingContext2D,
  size: number,
  strength: number,
  warm = 0,
  seed = 1
): void {
  if (strength <= 0) return;
  const pattern = ctx.createPattern(grainTile(strength, warm, seed), 'repeat');
  if (!pattern) return;
  ctx.save();
  ctx.globalCompositeOperation = 'overlay';
  ctx.fillStyle = pattern;
  ctx.fillRect(0, 0, size, size);
  ctx.restore();
}

/**
 * Convert an already-drawn colour canvas into a height field. Reusing the
 * colour pass means the bump map is guaranteed to register with the map —
 * grout grooves land exactly on grout lines, never a pixel off.
 *
 * The pass auto-centres on the image's own mean luminance before applying
 * contrast. Without that, a warm-cream floor (mean luminance ~0.9) clips to a
 * flat white or, inverted, a flat black — a height map with no height in it.
 *
 * This IS a full-resolution pass, so it only runs on the opt-in `bump` channel.
 */
function toHeight(
  ctx: CanvasRenderingContext2D,
  size: number,
  opts: { contrast?: number; invert?: boolean; bias?: number } = {}
): void {
  const contrast = opts.contrast ?? 1.6;
  const bias = opts.bias ?? 0;
  const img = ctx.getImageData(0, 0, size, size);
  const d = img.data;

  let sum = 0;
  for (let i = 0; i < d.length; i += 4) {
    sum += d[i]! * 0.2126 + d[i + 1]! * 0.7152 + d[i + 2]! * 0.0722;
  }
  const mean = sum / (d.length / 4) / 255;

  for (let i = 0; i < d.length; i += 4) {
    let l = (d[i]! * 0.2126 + d[i + 1]! * 0.7152 + d[i + 2]! * 0.0722) / 255;
    // Recentre on the mean, then expand — so the *variation* becomes relief.
    l = (l - mean) * contrast * 2.2 + 0.5 + bias;
    if (opts.invert) l = 1 - l;
    const v = l * 255;
    d[i] = v;
    d[i + 1] = v;
    d[i + 2] = v;
    d[i + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
}

/* ------------------------------------------------------------------ *
 * Cache + bake pipeline
 * ------------------------------------------------------------------ */

const cache = new Map<string, CanvasTexture>();
const registry: Array<{ key: string; label: string; texture: CanvasTexture }> = [];
let globalAnisotropy = 8;

/**
 * Raise anisotropic filtering to the device maximum. Three clamps to the GPU
 * limit at upload time, so passing `renderer.capabilities.getMaxAnisotropy()`
 * is always safe. Call once after the renderer exists; already-baked textures
 * are upgraded in place.
 */
export function setForgeAnisotropy(max: number): void {
  globalAnisotropy = Math.max(1, Math.floor(max));
  for (const tex of cache.values()) {
    tex.anisotropy = globalAnisotropy;
    tex.needsUpdate = true;
  }
}

/** Every texture baked so far, for the forge demo and for debugging. */
export function bakedTextures(): ReadonlyArray<{
  key: string;
  label: string;
  texture: CanvasTexture;
}> {
  return registry;
}

/** App-teardown only. Scenes must NOT call this; textures are shared globally. */
export function disposeForgeTextures(): void {
  for (const tex of cache.values()) tex.dispose();
  cache.clear();
  registry.length = 0;
  grainTiles.clear();
}

export type WrapMode = 'repeat' | 'clamp' | 'mirror';

const WRAP_MODES: Record<WrapMode, Wrapping> = {
  repeat: RepeatWrapping,
  clamp: ClampToEdgeWrapping,
  mirror: MirroredRepeatWrapping,
};

/** Options every surface generator understands. */
export interface SurfaceOptions {
  /** Canvas edge length in pixels. Powers of two only, for clean mips. */
  size?: number;
  /** Reproducibility knob — same seed, same texture, forever. */
  seed?: number;
  /** UV repeat. A scalar sets both axes. */
  repeat?: number | [number, number];
  wrap?: WrapMode;
  /** Per-texture anisotropy override; defaults to the global forge setting. */
  anisotropy?: number;
  /**
   * `'color'` bakes the albedo map (sRGB). `'bump'` bakes the same pattern as a
   * grayscale height field (linear), registered pixel-for-pixel with the albedo
   * so it can be dropped straight into `material.bumpMap`.
   */
  channel?: 'color' | 'bump';
}

interface BakeConfig {
  kind: string;
  /** Fully resolved options — this object IS the cache key. */
  key: Record<string, string | number | boolean>;
  label: string;
  size: number;
  seed: number;
  /** Linear data (height/mask) rather than colour. */
  data: boolean;
  wrap: Wrapping;
  repeat: [number, number];
  anisotropy: number;
  draw: (ctx: CanvasRenderingContext2D, size: number, rand: () => number) => void;
}

function bake(cfg: BakeConfig): CanvasTexture {
  const sortedKeys = Object.keys(cfg.key).sort();
  const parts: string[] = [cfg.kind];
  for (const k of sortedKeys) parts.push(`${k}=${String(cfg.key[k])}`);
  const cacheKey = parts.join('|');

  const hit = cache.get(cacheKey);
  if (hit) return hit;

  const { canvas, ctx } = make2d(cfg.size, cfg.size);
  cfg.draw(ctx, cfg.size, mulberry32(cfg.seed ^ hashString(cfg.kind)));

  const tex = new CanvasTexture(canvas);
  // Colour maps must be tagged sRGB or the palette washes out; height and mask
  // maps must stay linear or the gamma curve doubles their contrast.
  tex.colorSpace = cfg.data ? NoColorSpace : SRGBColorSpace;
  tex.wrapS = cfg.wrap;
  tex.wrapT = cfg.wrap;
  tex.repeat.set(cfg.repeat[0], cfg.repeat[1]);
  tex.anisotropy = cfg.anisotropy;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  tex.name = cfg.label;

  cache.set(cacheKey, tex);
  registry.push({ key: cacheKey, label: cfg.label, texture: tex });
  return tex;
}

/** Normalise the shared surface options into concrete bake parameters. */
function common(o: SurfaceOptions, defaults: { size?: number; wrap?: WrapMode } = {}) {
  const size = o.size ?? defaults.size ?? 512;
  const rep = o.repeat ?? 1;
  const repeat: [number, number] = typeof rep === 'number' ? [rep, rep] : rep;
  const wrapMode = o.wrap ?? defaults.wrap ?? 'repeat';
  const channel = o.channel ?? 'color';
  const seed = o.seed ?? 1337;
  const anisotropy = o.anisotropy ?? globalAnisotropy;
  return {
    size,
    seed,
    repeat,
    wrap: WRAP_MODES[wrapMode],
    anisotropy,
    channel,
    data: channel === 'bump',
    /** The slice of the cache key contributed by the shared options. */
    keyBase: {
      size,
      seed,
      rx: repeat[0],
      ry: repeat[1],
      wrap: wrapMode,
      aniso: anisotropy,
      channel,
    } as Record<string, string | number | boolean>,
  };
}

/* ================================================================== *
 * 1. WARM-CREAM FLOOR — paper tooth + hand-drawn plank / tile seams
 * ================================================================== */

export interface FloorTextureOptions extends SurfaceOptions {
  base?: number;
  seamColor?: number;
  /** `plank` for living room / bedroom, `tile` for kitchen / bathroom. */
  pattern?: 'plank' | 'tile' | 'plain';
  /** Planks (rows) or tiles (per axis) inside one texture tile. */
  cells?: number;
  /** 0..1 how visible the paper fibre is. */
  tooth?: number;
  /** 0..1 how much large-scale painted mottle sits under the tooth. */
  mottle?: number;
}

export function creamFloorTexture(o: FloorTextureOptions = {}): CanvasTexture {
  const c = common(o);
  const base = o.base ?? SURFACE.floor;
  const seamColor = o.seamColor ?? SURFACE.floorSeam;
  const pattern = o.pattern ?? 'plank';
  const cells = Math.max(1, Math.round(o.cells ?? 4));
  const tooth = o.tooth ?? 0.06;
  const mottle = o.mottle ?? 0.45;

  return bake({
    kind: 'floor',
    label: `floor:${pattern}`,
    key: { ...c.keyBase, base, seamColor, pattern, cells, tooth, mottle },
    size: c.size,
    seed: c.seed,
    data: c.data,
    wrap: c.wrap,
    repeat: c.repeat,
    anisotropy: c.anisotropy,
    draw: (ctx, size, rand) => {
      ctx.fillStyle = css(base);
      ctx.fillRect(0, 0, size, size);

      // Broad painted variation. Never a single flat fill — a perfectly even
      // cream field is the fastest way to make a floor look like a colour swatch.
      const blotch = tileFbm(rand, 3, 3);
      const N = 28;
      paintField(ctx, size, N, SURFACE.floorLift, SURFACE.floorDeep, mottle, (i, j) =>
        (blotch(i / N, j / N) - 0.5) * 2
      );

      const wob = tileNoise(rand, 12);
      const cell = size / cells;

      if (pattern === 'plank') {
        // Long boards running in X, with staggered end joints. Each board gets
        // its own faint tone so the floor reads as separate pieces of timber.
        for (let row = 0; row < cells; row++) {
          const y = row * cell;
          const tone = (rand() - 0.5) * 0.05;
          ctx.fillStyle = css(tone >= 0 ? SURFACE.floorLift : SURFACE.floorDeep, Math.abs(tone) * 4);
          ctx.fillRect(0, y, size, cell);

          // Shadow line then a highlight beneath: a carved groove, not a stripe.
          seamH(ctx, size, y, wob, {
            color: seamColor,
            alpha: 0.62,
            width: Math.max(2, size / 260),
            amp: size / 380,
            lane: row * 0.19,
          });
          seamH(ctx, size, y + Math.max(2.5, size / 190), wob, {
            color: SURFACE.floorLift,
            alpha: 0.6,
            width: Math.max(1.2, size / 480),
            amp: size / 380,
            lane: row * 0.19,
          });

          for (let j = 0; j < 2; j++) {
            const x = ((j + (row % 2 ? 0.5 : 0.15) + rand() * 0.2) / 2) * size;
            seamV(ctx, size, x, y + cell * 0.06, y + cell * 0.94, wob, {
              color: seamColor,
              alpha: 0.5,
              width: Math.max(1.6, size / 320),
              amp: size / 700,
              lane: 0.31 + j * 0.27,
            });
          }
        }
      } else if (pattern === 'tile') {
        for (let i = 0; i <= cells; i++) {
          const p = i * cell;
          seamH(ctx, size, p, wob, {
            color: seamColor,
            alpha: 0.58,
            width: Math.max(2.5, size / 210),
            amp: size / 520,
            lane: i * 0.23,
          });
          seamV(ctx, size, p, 0, size, wob, {
            color: seamColor,
            alpha: 0.58,
            width: Math.max(2.5, size / 210),
            amp: size / 520,
            lane: 0.7 + i * 0.23,
            steps: 48,
          });
        }
      }

      // A few dust specks and fibre knots, wrapped so they survive tiling.
      const specks = Math.round(size / 14);
      for (let i = 0; i < specks; i++) {
        const r = 0.6 + rand() * (size / 420);
        const dark = rand() > 0.4;
        const alpha = 0.16 + rand() * 0.12;
        ctx.fillStyle = css(dark ? SURFACE.floorSeam : SURFACE.floorLift, alpha);
        wrapMark(size, rand() * size, rand() * size, r + 2, (cx, cy) => {
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.fill();
        });
      }

      // Paper tooth: fine warm fibre over everything.
      applyGrain(ctx, size, tooth, 6, c.seed);

      if (c.data) toHeight(ctx, size, { contrast: 1.35, invert: true });
    },
  });
}

/* ================================================================== *
 * 2. PAINTED WOOD — brand-green furniture paint over visible grain
 * ================================================================== */

export interface WoodTextureOptions extends SurfaceOptions {
  base?: number;
  grainColor?: number;
  /** Number of grain lines across the tile. */
  grainCount?: number;
  /** 0..1 grain visibility through the paint. */
  grainStrength?: number;
  knots?: number;
  /** 0..1 loose brush-stroke sheen from the paint pass. */
  brush?: number;
}

export function paintedWoodTexture(o: WoodTextureOptions = {}): CanvasTexture {
  const c = common(o);
  const base = o.base ?? SURFACE.wood;
  const grainColor = o.grainColor ?? SURFACE.woodGrain;
  const grainCount = Math.max(4, Math.round(o.grainCount ?? 26));
  const grainStrength = o.grainStrength ?? 0.35;
  const knots = Math.max(0, Math.round(o.knots ?? 2));
  const brush = o.brush ?? 0.4;

  return bake({
    kind: 'wood',
    label: 'painted-wood',
    key: { ...c.keyBase, base, grainColor, grainCount, grainStrength, knots, brush },
    size: c.size,
    seed: c.seed,
    data: c.data,
    wrap: c.wrap,
    repeat: c.repeat,
    anisotropy: c.anisotropy,
    draw: (ctx, size, rand) => {
      ctx.fillStyle = css(base);
      ctx.fillRect(0, 0, size, size);

      const mott = tileFbm(rand, 4, 2);
      const N = 24;
      paintField(ctx, size, N, SURFACE.woodLift, SURFACE.woodGrain, 0.3, (i, j) =>
        (mott(i / N, j / N) - 0.5) * 2
      );

      const wave = tileNoise(rand, 8);

      // Grain runs along X. Lines are drawn at three offsets in Y so any line
      // that wanders past an edge re-enters cleanly from the other side.
      for (let i = 0; i < grainCount; i++) {
        const y = ((i + rand() * 0.6) / grainCount) * size;
        const amp = size * (0.004 + rand() * 0.016);
        const a = grainStrength * (0.25 + rand() * 0.75);
        const w = Math.max(1, size / 420) * (0.6 + rand() * 1.8);
        const lane = rand();
        const col = rand() > 0.75 ? SURFACE.woodLift : grainColor;
        for (let k = -1; k <= 1; k++) {
          seamH(ctx, size, y + k * size, wave, { color: col, alpha: a, width: w, amp, lane, steps: 40 });
        }
      }

      // Knots — concentric wobbly ellipses, wrapped across the tile border.
      for (let i = 0; i < knots; i++) {
        const kx = rand() * size;
        const ky = rand() * size;
        const kr = size * (0.02 + rand() * 0.03);
        const tilt = rand() * 0.5;
        ctx.strokeStyle = css(grainColor, 0.16 + grainStrength * 0.25);
        ctx.lineWidth = Math.max(1, size / 500);
        wrapMark(size, kx, ky, kr + 4, (cx, cy) => {
          for (let r = kr; r > kr * 0.2; r -= Math.max(1.5, size / 300)) {
            ctx.beginPath();
            ctx.ellipse(cx, cy, r, r * 0.6, tilt, 0, Math.PI * 2);
            ctx.stroke();
          }
        });
      }

      // Loose brush sheen — long soft strokes of slightly lifted paint.
      const strokes = Math.round(10 * brush) + 4;
      for (let i = 0; i < strokes; i++) {
        const y = rand() * size;
        const h = size * (0.01 + rand() * 0.05);
        const g = ctx.createLinearGradient(0, y, 0, y + h);
        g.addColorStop(0, css(SURFACE.woodLift, 0));
        g.addColorStop(0.5, css(SURFACE.woodLift, 0.1 * brush));
        g.addColorStop(1, css(SURFACE.woodLift, 0));
        ctx.fillStyle = g;
        ctx.fillRect(0, y, size, h);
      }

      applyGrain(ctx, size, 0.05, 3, c.seed + 1);

      if (c.data) toHeight(ctx, size, { contrast: 1.9, invert: true });
    },
  });
}

/* ================================================================== *
 * 3. WOVEN FABRIC — sofa, bed, cushions
 * ================================================================== */

export interface FabricTextureOptions extends SurfaceOptions {
  base?: number;
  warp?: number;
  weft?: number;
  /**
   * Requested thread crossings per axis across the tile, treated as a MINIMUM.
   * See `MIN_WEAVE_THREADS` for why a caller can no longer ask for a coarse
   * weave.
   */
  threads?: number;
  weave?: 'plain' | 'twill';
  /** 0..1 loose fibre fuzz on top of the weave. */
  fuzz?: number;
  /**
   * 0..1 how much of the caller's warp/weft separation from `base` survives.
   * See `WEAVE_CONTRAST` — this is the second half of the "wire mesh" fix and
   * the knob to reach for if fabric ever looks flat again.
   */
  contrast?: number;
}

/**
 * ── The "there are like grids showing" defect, 2026-08-06 ────────────────────
 *
 * The user looked at Game 1 and reported a repeating crosshatch on the green
 * mats and rugs that reads as WIRE MESH rather than as cloth. Measured, at
 * 1600x900 / high tier / the settled gameplay camera:
 *
 *   world units per screen pixel                        0.00724
 *   one `rug`/`softGreen` fabric tile on screen         ~250-265 px
 *   `threads: 20` from scenes/game1/rooms.ts            -> 12.5 px per crossing
 *   texels per screen pixel                             ~2.0
 *
 * A 12px thread crossing is a mark you can COUNT. That is the whole defect.
 *
 * It is NOT a minification / mip / anisotropy problem, and the obvious fix of
 * "raise the anisotropy" would have done nothing. `tools/fabricprobe.mjs` shot
 * the same frame at anisotropy 1, 8 and 16, and again with NearestFilter and
 * mipmaps switched off entirely: the lattice is pixel-for-pixel the same
 * lattice in every condition. At ~2 texels per pixel the weave is comfortably
 * oversampled, so the mip chain never gets the chance to average it away — the
 * pattern is simply drawn too large and too contrasty to be a texture.
 *
 * Two constants fix it, and they have to live here because the thread count is
 * passed in by `src/scenes/game1/rooms.ts`, which this module does not own.
 */

/**
 * Floor on weave frequency, in thread crossings per tile.
 *
 * At 64 the crossing lands at ~4 screen px under the gameplay camera and ~1 px
 * at the whole-house overview — fine enough to read as tooth and to let the mip
 * chain resolve it into flat tone as the camera pulls back, which is exactly
 * what the "flat colour with paper tooth" art direction asks for. Callers may
 * ask for MORE (a fine linen), never less.
 */
const MIN_WEAVE_THREADS = 64;

/**
 * Fraction of the caller's warp/weft separation from `base` that survives.
 *
 * `rooms.ts` derives its yarn colours as `shadeHex(color, -0.24)` and
 * `shadeHex(color, +0.26)` — a full half-stop of value between neighbouring
 * threads. That is upholstery-swatch contrast, and against a strict lattice it
 * is what turns weave into mesh. Damping it to a third leaves the surface
 * reading as woven without any single thread announcing itself.
 */
const WEAVE_CONTRAST = 0.34;

export function wovenFabricTexture(o: FabricTextureOptions = {}): CanvasTexture {
  const c = common(o);
  const base = o.base ?? SURFACE.fabric;
  const contrast = Math.max(0, Math.min(1, o.contrast ?? WEAVE_CONTRAST));
  // Damp toward `base` rather than replacing the caller's hues: the yarns stay
  // the colours the palette derived, just closer together in value.
  const warp = mixHex(base, o.warp ?? SURFACE.fabricWarp, contrast);
  const weft = mixHex(base, o.weft ?? SURFACE.fabricWeft, contrast);
  const weave = o.weave ?? 'plain';
  const fuzz = o.fuzz ?? 0.5;
  const requested = Math.max(4, Math.round(o.threads ?? 24));

  // The weave motif is stamped as a repeating canvas pattern, so it must divide
  // the tile exactly or the texture seams. Snapping the cell to a power of two
  // guarantees that for any power-of-two `size`, which the forge already
  // requires. `period` is the weave's own repeat: 2 for plain, 4 for a 2/2
  // twill (the classic denim weave — 3 was used before and does not divide a
  // power of two, which is why the period is 4 now and not 3).
  const period = weave === 'plain' ? 2 : 4;
  const wanted = c.size / Math.max(requested, MIN_WEAVE_THREADS);
  const cell = Math.max(2, Math.pow(2, Math.floor(Math.log2(wanted))));
  const threads = Math.round(c.size / cell);
  const motif = cell * period;

  return bake({
    kind: 'fabric',
    label: `fabric:${weave}`,
    key: { ...c.keyBase, base, warp, weft, threads, weave, fuzz, contrast },
    size: c.size,
    seed: c.seed,
    data: c.data,
    wrap: c.wrap,
    repeat: c.repeat,
    anisotropy: c.anisotropy,
    draw: (ctx, size, rand) => {
      ctx.fillStyle = css(base);
      ctx.fillRect(0, 0, size, size);

      /* ---- one weave period, stamped as a pattern ---------------------- *
       * The old code drew `threads * threads` rounded rects — 400 filled
       * paths at threads=20, and 4096 at the density this now needs. A weave
       * is exactly periodic, so only `period * period` cells are ever
       * distinct: bake those once and let the rasteriser repeat them. Cost is
       * O(1) in thread count, which is what makes the finer weave free.
       * ------------------------------------------------------------------ */
      const { canvas: tile, ctx: tc } = make2d(motif, motif);
      const pad = cell * 0.08;
      const r = cell * 0.32;

      const overGrad = tc.createLinearGradient(0, 0, 0, cell);
      overGrad.addColorStop(0, css(mixHex(weft, HEX.ink, 0.12), 0.85));
      overGrad.addColorStop(0.45, css(weft, 0.95));
      overGrad.addColorStop(1, css(mixHex(weft, HEX.ink, 0.16), 0.85));
      const underGrad = tc.createLinearGradient(0, 0, cell, 0);
      underGrad.addColorStop(0, css(mixHex(warp, HEX.ink, 0.12), 0.85));
      underGrad.addColorStop(0.45, css(warp, 0.95));
      underGrad.addColorStop(1, css(mixHex(warp, HEX.ink, 0.16), 0.85));

      tc.fillStyle = css(base);
      tc.fillRect(0, 0, motif, motif);
      for (let j = 0; j < period; j++) {
        for (let i = 0; i < period; i++) {
          // Plain alternates every crossing; the 2/2 twill floats two threads
          // then passes under two, stepping one per row — the diagonal rib.
          const over = weave === 'plain' ? (i + j) % 2 === 0 : (i + j) % 4 < 2;
          tc.save();
          tc.translate(i * cell, j * cell);
          tc.fillStyle = over ? overGrad : underGrad;
          if (over) rrect(tc, -pad, pad, cell + pad * 2, cell - pad * 2, r);
          else rrect(tc, pad, -pad, cell - pad * 2, cell + pad * 2, r);
          tc.fill();
          tc.restore();
        }
      }

      const pattern = ctx.createPattern(tile, 'repeat');
      if (pattern) {
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, size, size);
      }

      // Low-frequency dye/wear variation. The pattern stamp is perfectly
      // periodic by construction, so without a non-periodic field on top the
      // cloth would be flawless in a way no dyed cloth ever is. This is the
      // variation the old per-cell `jitter` used to supply, moved to a scale
      // the eye reads as fabric rather than as noise.
      const wear = tileFbm(rand, 4, 2);
      paintField(ctx, size, 24, HEX.white, HEX.ink, 0.05, (i, j) => wear(i / 24, j / 24) - 0.5);

      // Fibre fuzz: short random hairs that break the lattice.
      const hairs = Math.round(size * 0.5 * fuzz);
      ctx.lineCap = 'round';
      ctx.lineWidth = Math.max(0.7, size / 700);
      for (let i = 0; i < hairs; i++) {
        const a = rand() * Math.PI * 2;
        const len = cell * (1.4 + rand() * 3.5);
        const dx = Math.cos(a) * len;
        const dy = Math.sin(a) * len;
        ctx.strokeStyle = css(rand() > 0.5 ? weft : warp, 0.16);
        wrapMark(size, rand() * size, rand() * size, len + 2, (cx, cy) => {
          ctx.beginPath();
          ctx.moveTo(cx, cy);
          ctx.lineTo(cx + dx, cy + dy);
          ctx.stroke();
        });
      }

      applyGrain(ctx, size, 0.06, 4, c.seed + 2);

      if (c.data) toHeight(ctx, size, { contrast: 2.1 });
    },
  });
}

/* ================================================================== *
 * 4. CERAMIC TILE — bathroom + kitchen splashback
 * ================================================================== */

export interface TileTextureOptions extends SurfaceOptions {
  base?: number;
  grout?: number;
  tiles?: number;
  /** 0..1 specular sheen swept across each tile. */
  gloss?: number;
  /** 0..1 per-tile tone variation, the tell that these were fired not printed. */
  variation?: number;
  speckle?: number;
  groutWidth?: number;
  /**
   * 0..1 how much of the caller's grout/base separation survives.
   * See `GROUT_CONTRAST` — this is the tile half of the "grids showing" fix.
   */
  contrast?: number;
}

/**
 * Fraction of the caller's grout-to-base value separation that survives.
 *
 * The second half of the user's 2026-08-06 "there are like grids showing"
 * report. `scenes/game1/rooms.ts` asks for `shadeHex(color, -0.32)`, and this
 * generator then lays that value down THREE times on the same joint: as the
 * base fill showing through the gap, as a wobbled seam stroke at 0.5 alpha, and
 * again as a per-tile bevel stroke at 0.35 alpha. The result is a hard lattice
 * across the whole kitchen and bathroom floor — 34 screen px per cell at the
 * gameplay camera and 9 px at the whole-house overview, so unlike the weave
 * there is no zoom at which it stops being a countable grid.
 *
 * Frequency is the wrong lever here: a bathroom tile really is ~25cm, and the
 * approved reference draws these floors as flat cream with no joint at all. So
 * the joint stays where a tile joint belongs and simply stops shouting. Value
 * and width both had to come down: damping the value to 0.42 on its own still
 * read as a lattice at the overview, because a joint 3.5 screen px wide is a
 * drawn line however pale it is. At 0.26 with
 * `groutWidth` 0.038 the floor reads as tiled close up and mips into flat tone
 * as the camera pulls back, which is what the "flat colour with paper tooth"
 * art direction asks for.
 */
const GROUT_CONTRAST = 0.26;

export function ceramicTileTexture(o: TileTextureOptions = {}): CanvasTexture {
  const c = common(o);
  const base = o.base ?? SURFACE.tile;
  const contrast = Math.max(0, Math.min(1, o.contrast ?? GROUT_CONTRAST));
  // Damped toward `base`, so the joint keeps the hue the palette derived for it
  // and only gives up value.
  const grout = mixHex(base, o.grout ?? SURFACE.grout, contrast);
  const tiles = Math.max(1, Math.round(o.tiles ?? 4));
  const gloss = o.gloss ?? 0.45;
  const variation = o.variation ?? 0.35;
  const speckle = o.speckle ?? 0.4;
  const groutWidth = o.groutWidth ?? 0.038;

  return bake({
    kind: 'tile',
    label: 'ceramic-tile',
    key: { ...c.keyBase, base, grout, tiles, gloss, variation, speckle, groutWidth, contrast },
    size: c.size,
    seed: c.seed,
    data: c.data,
    wrap: c.wrap,
    repeat: c.repeat,
    anisotropy: c.anisotropy,
    draw: (ctx, size, rand) => {
      ctx.fillStyle = css(grout);
      ctx.fillRect(0, 0, size, size);

      const cell = size / tiles;
      const gap = cell * groutWidth;
      const wob = tileNoise(rand, 10);
      const w = cell - gap * 2;
      const h = cell - gap * 2;

      // Glaze sweep, authored once in tile-local space then translated. Diagonal
      // and offset per tile so the wall does not read as one big gradient.
      const glazeGrad = ctx.createLinearGradient(0, 0, w, h);
      glazeGrad.addColorStop(0, css(HEX.white, 0.28 * gloss));
      glazeGrad.addColorStop(0.34, css(HEX.white, 0.06 * gloss));
      glazeGrad.addColorStop(0.62, css(HEX.white, 0.2 * gloss));
      glazeGrad.addColorStop(1, css(grout, 0.14 * gloss));

      for (let j = 0; j < tiles; j++) {
        for (let i = 0; i < tiles; i++) {
          const x = i * cell + gap;
          const y = j * cell + gap;
          const tone = (rand() - 0.5) * 2 * variation;
          const face =
            tone >= 0 ? mixHex(base, HEX.white, tone * 0.4) : mixHex(base, HEX.ink, -tone * 0.16);

          ctx.save();
          ctx.translate(x, y);
          rrect(ctx, 0, 0, w, h, cell * 0.06);
          ctx.fillStyle = css(face);
          ctx.fill();
          if (gloss > 0) {
            ctx.save();
            ctx.clip();
            ctx.fillStyle = glazeGrad;
            ctx.fillRect(0, 0, w, h);
            ctx.restore();
          }
          // Chipped-glaze bevel: an ink hairline that follows the tile edge.
          // Alpha halved with the grout damping — this stroke sits directly on
          // top of the joint, so at 0.35 it was re-darkening the very line the
          // damping is there to soften.
          rrect(ctx, 0.5, 0.5, w - 1, h - 1, cell * 0.06);
          ctx.strokeStyle = css(grout, 0.17);
          ctx.lineWidth = Math.max(1, size / 700);
          ctx.stroke();
          ctx.restore();
        }
      }

      // Hand-drawn grout wobble on top of the machined grid. The wobble is the
      // whole point — it is what stops the joint reading as a `strokeRect` —
      // but at 0.5 alpha it was the darkest of the three passes over the joint,
      // so it carries the damping too.
      for (let i = 0; i <= tiles; i++) {
        const p = i * cell;
        seamH(ctx, size, p, wob, {
          color: grout,
          alpha: 0.3,
          width: gap * 0.9,
          amp: gap * 0.28,
          lane: i * 0.29,
        });
        seamV(ctx, size, p, 0, size, wob, {
          color: grout,
          alpha: 0.3,
          width: gap * 0.9,
          amp: gap * 0.28,
          lane: 0.6 + i * 0.29,
          steps: 40,
        });
      }

      const dots = Math.round(size * 0.5 * speckle);
      ctx.fillStyle = css(grout, 0.1);
      const dr = Math.max(0.6, size / 900);
      for (let i = 0; i < dots; i++) {
        wrapMark(size, rand() * size, rand() * size, dr + 2, (cx, cy) => {
          ctx.beginPath();
          ctx.arc(cx, cy, dr, 0, Math.PI * 2);
          ctx.fill();
        });
      }

      applyGrain(ctx, size, 0.03, 0, c.seed + 3);

      if (c.data) toHeight(ctx, size, { contrast: 1.5, invert: true });
    },
  });
}

/* ================================================================== *
 * 5. BRUSHED METAL — factory chassis, conveyor rails, appliance doors
 * ================================================================== */

export interface MetalTextureOptions extends SurfaceOptions {
  base?: number;
  /** 0..1 density of the brushed streaks. */
  brush?: number;
  /** Direction the abrasive ran. */
  direction?: 'horizontal' | 'vertical';
  /** Periodic sheen bands — count across the tile (kept integral so it tiles). */
  bands?: number;
  rivets?: number;
}

export function brushedMetalTexture(o: MetalTextureOptions = {}): CanvasTexture {
  const c = common(o);
  const base = o.base ?? SURFACE.metal;
  const brush = o.brush ?? 0.6;
  const direction = o.direction ?? 'horizontal';
  const bands = Math.max(0, Math.round(o.bands ?? 3));
  const rivets = Math.max(0, Math.round(o.rivets ?? 0));

  return bake({
    kind: 'metal',
    label: 'brushed-metal',
    key: { ...c.keyBase, base, brush, direction, bands, rivets },
    size: c.size,
    seed: c.seed,
    data: c.data,
    wrap: c.wrap,
    repeat: c.repeat,
    anisotropy: c.anisotropy,
    draw: (ctx, size, rand) => {
      ctx.fillStyle = css(base);
      ctx.fillRect(0, 0, size, size);

      // Sheen bands from a sine, so the pattern is periodic and tiles exactly.
      // A linear gradient across the tile would leave a hard join at the seam.
      if (bands > 0) {
        const N = bands * 12;
        paintField(ctx, size, N, SURFACE.metalLift, SURFACE.metalDark, 0.22, (i, j) => {
          const t = (direction === 'horizontal' ? j : i) / N;
          return Math.sin(t * Math.PI * 2 * bands);
        });
      }

      // Abrasive streaks — full-span lines so they tile without ends showing.
      const streaks = Math.round(size * 1.2 * brush);
      ctx.lineCap = 'butt';
      for (let i = 0; i < streaks; i++) {
        const p = rand() * size;
        const light = rand() > 0.5;
        ctx.strokeStyle = css(light ? SURFACE.metalLift : SURFACE.metalDark, 0.03 + rand() * 0.09);
        ctx.lineWidth = Math.max(0.6, rand() * (size / 380));
        ctx.beginPath();
        if (direction === 'horizontal') {
          ctx.moveTo(0, p);
          ctx.lineTo(size, p);
        } else {
          ctx.moveTo(p, 0);
          ctx.lineTo(p, size);
        }
        ctx.stroke();
      }

      // Rivets sit on a lattice so they survive tiling by construction.
      if (rivets > 0) {
        const step = size / rivets;
        const rr = Math.max(2, size / 90);
        for (let j = 0; j < rivets; j++) {
          for (let i = 0; i < rivets; i++) {
            const x = (i + 0.5) * step;
            const y = (j + 0.5) * step;
            const g = ctx.createRadialGradient(x - rr * 0.3, y - rr * 0.3, rr * 0.1, x, y, rr);
            g.addColorStop(0, css(SURFACE.metalLift, 0.95));
            g.addColorStop(1, css(SURFACE.metalDark, 0.9));
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(x, y, rr, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = css(HEX.ink, 0.35);
            ctx.lineWidth = Math.max(1, size / 480);
            ctx.stroke();
          }
        }
      }

      applyGrain(ctx, size, 0.04, 0, c.seed + 4);

      if (c.data) toHeight(ctx, size, { contrast: 2.4 });
    },
  });
}

/* ================================================================== *
 * 6. GLOSSY PLASTIC — bottles, bins, moulded housings
 * ================================================================== */

export interface PlasticTextureOptions extends SurfaceOptions {
  base?: number;
  /** 0..1 sheen strength. PET is high, HDPE is matte and waxy. */
  gloss?: number;
  /** Highlight bands per tile — integral so it tiles. */
  bands?: number;
  /** 0..1 cloudiness. HDPE / LDPE need this; PET does not. */
  cloud?: number;
  /** Moulding micro-scratches. */
  scuffs?: number;
  /** 0..1 tiny trapped bubbles, the tell of recycled resin. */
  bubbles?: number;
}

export function glossyPlasticTexture(o: PlasticTextureOptions = {}): CanvasTexture {
  const c = common(o);
  const base = o.base ?? SURFACE.plastic;
  const gloss = o.gloss ?? 0.6;
  const bands = Math.max(0, Math.round(o.bands ?? 2));
  const cloud = o.cloud ?? 0.25;
  const scuffs = Math.max(0, Math.round(o.scuffs ?? 24));
  const bubbles = o.bubbles ?? 0.3;

  return bake({
    kind: 'plastic',
    label: 'glossy-plastic',
    key: { ...c.keyBase, base, gloss, bands, cloud, scuffs, bubbles },
    size: c.size,
    seed: c.seed,
    data: c.data,
    wrap: c.wrap,
    repeat: c.repeat,
    anisotropy: c.anisotropy,
    draw: (ctx, size, rand) => {
      ctx.fillStyle = css(base);
      ctx.fillRect(0, 0, size, size);

      // Cloudiness first — it sits *under* the gloss, like resin under a skin.
      if (cloud > 0) {
        const n = tileFbm(rand, 4, 3);
        const N = 32;
        paintField(ctx, size, N, HEX.white, HEX.white, cloud, (i, j) =>
          Math.max(0, n(i / N, j / N) - 0.35)
        );
      }

      // Vertical specular bands — a moulded bottle catches light in stripes.
      if (bands > 0 && gloss > 0) {
        const shade = mixHex(base, HEX.deepGreen, 0.35);
        const N = bands * 20;
        paintField(ctx, size, N, HEX.white, shade, gloss, (i) => {
          const s = Math.sin((i / N) * Math.PI * 2 * bands);
          return s >= 0 ? Math.pow(s, 6) * 0.85 : -Math.pow(-s, 3) * 0.28;
        });
      }

      ctx.lineCap = 'round';
      ctx.lineWidth = Math.max(0.7, size / 640);
      for (let i = 0; i < scuffs; i++) {
        const a = (rand() - 0.5) * 0.5 + Math.PI / 2;
        const len = size * (0.03 + rand() * 0.16);
        const dx = Math.cos(a) * len;
        const dy = Math.sin(a) * len;
        ctx.strokeStyle = css(HEX.white, 0.1 + rand() * 0.14);
        wrapMark(size, rand() * size, rand() * size, len + 2, (cx, cy) => {
          ctx.beginPath();
          ctx.moveTo(cx, cy);
          ctx.lineTo(cx + dx, cy + dy);
          ctx.stroke();
        });
      }

      const bcount = Math.round(size * 0.15 * bubbles);
      ctx.strokeStyle = css(HEX.white, 0.28);
      ctx.lineWidth = Math.max(0.6, size / 900);
      for (let i = 0; i < bcount; i++) {
        const r = Math.max(1, size / 500) * (1 + rand() * 2.5);
        wrapMark(size, rand() * size, rand() * size, r + 2, (cx, cy) => {
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.stroke();
        });
      }

      applyGrain(ctx, size, 0.022, 0, c.seed + 5);

      if (c.data) toHeight(ctx, size, { contrast: 1.2 });
    },
  });
}

/* ================================================================== *
 * 7. HAND-INKED HATCHING / STIPPLE OVERLAY
 * ------------------------------------------------------------------
 * Shadow texture. Cel shading gives hard bands; hatching gives those bands the
 * *material* of a pen. Use as a transparent overlay map (`polarity: 'ink'`) or
 * as an alphaMap / bumpMap (`polarity: 'mask'`).
 * ================================================================== */

export interface HatchTextureOptions extends SurfaceOptions {
  style?: 'hatch' | 'crosshatch' | 'stipple' | 'mixed';
  /** Lines (or dots-per-row) across the tile. Higher = denser shadow. */
  density?: number;
  /** Only 0 / 45 / 90 / 135 tile exactly; anything else snaps to the nearest. */
  angleDeg?: number;
  color?: number;
  /** 0..1 peak ink opacity. */
  strength?: number;
  /** Pen width in pixels at the reference 512 size. */
  weight?: number;
  /**
   * `'ink'` = transparent canvas with ink strokes (drop into `map` on an
   * overlay). `'mask'` = white strokes on black (drop into `alphaMap`/`bumpMap`).
   */
  polarity?: 'ink' | 'mask';
}

export function hatchOverlayTexture(o: HatchTextureOptions = {}): CanvasTexture {
  const c = common(o);
  const style = o.style ?? 'hatch';
  const density = Math.max(2, Math.round(o.density ?? 22));
  const raw = ((((o.angleDeg ?? 45) % 180) + 180) % 180);
  // Snap to the four angles whose line lattice is commensurate with the tile.
  const angle = ([0, 45, 90, 135] as const).reduce((best, a) =>
    Math.abs(raw - a) < Math.abs(raw - best) ? a : best
  );
  const polarity = o.polarity ?? 'ink';
  const color = o.color ?? (polarity === 'mask' ? HEX.white : HEX.ink);
  const strength = o.strength ?? 0.6;
  const weight = o.weight ?? 2.4;

  return bake({
    kind: 'hatch',
    label: `hatch:${style}@${angle}`,
    key: { ...c.keyBase, style, density, angle, color, strength, weight, polarity },
    size: c.size,
    seed: c.seed,
    data: polarity === 'mask' || c.data,
    wrap: c.wrap,
    repeat: c.repeat,
    anisotropy: c.anisotropy,
    draw: (ctx, size, rand) => {
      if (polarity === 'mask') {
        ctx.fillStyle = css(HEX.ink);
        ctx.fillRect(0, 0, size, size);
      } else {
        ctx.clearRect(0, 0, size, size);
      }

      const px = (v: number): number => (v * size) / 512;
      const jig = tileNoise(rand, 16);

      /**
       * One family of parallel pen strokes. Lines sit on a lattice whose step
       * divides the tile, and each is a wobbling polyline rather than a straight
       * segment — the difference between a pen and a `strokeRect`.
       */
      const family = (deg: number, lanes: number, alphaScale: number): void => {
        const step = size / lanes;
        const rad = (deg * Math.PI) / 180;
        const dx = Math.cos(rad);
        const dy = Math.sin(rad);
        const nx = -dy;
        const ny = dx;
        const span = size * 2;
        const count = Math.ceil((size * 2) / step);

        ctx.lineCap = 'round';
        for (let i = -count; i <= count; i++) {
          const cx = size / 2 + nx * i * step;
          const cy = size / 2 + ny * i * step;
          ctx.strokeStyle = css(color, strength * alphaScale * (0.45 + rand() * 0.55));
          ctx.lineWidth = px(weight) * (0.55 + rand() * 0.9);
          const segs = 12;
          ctx.beginPath();
          for (let s = 0; s <= segs; s++) {
            const t = s / segs - 0.5;
            const jx = (jig(Math.abs(t) + i * 0.017, i * 0.041) - 0.5) * px(3.2);
            const jy = (jig(i * 0.041, Math.abs(t) + i * 0.017) - 0.5) * px(3.2);
            const x = cx + dx * t * span + nx * jx;
            const y = cy + dy * t * span + ny * jy;
            if (s === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.stroke();
        }
      };

      if (style === 'hatch' || style === 'mixed') family(angle, density, 1);
      if (style === 'crosshatch' || style === 'mixed') family((angle + 90) % 180, density, 0.75);

      if (style === 'stipple' || style === 'mixed') {
        const dots = Math.round(density * density * (style === 'mixed' ? 0.35 : 1.2));
        for (let i = 0; i < dots; i++) {
          const r = px(1.1) * (0.5 + rand() * 1.4);
          ctx.fillStyle = css(color, strength * (0.35 + rand() * 0.65));
          wrapMark(size, rand() * size, rand() * size, r + 2, (cx, cy) => {
            ctx.beginPath();
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.fill();
          });
        }
      }

      if (c.channel === 'bump' && polarity === 'ink') toHeight(ctx, size, { contrast: 1.4 });
    },
  });
}

/* ================================================================== *
 * 8. PAINTED-EDGE NOISE
 * ------------------------------------------------------------------
 * The anti-vector pass. A hard geometric boundary — the edge of a rug, a floor
 * plate, a decal, a shadow blob — screams "computed". This bakes a mask whose
 * boundary was drawn with a loaded brush: irregular, with bristle streaks and
 * dry-brush breakup where the paint skipped over the tooth of the paper.
 *
 * Apply as `alphaMap` on the surface whose edge should feel painted, or as a
 * `bumpMap` to make a cel terminator wobble like ink.
 * ================================================================== */

export interface PaintedEdgeOptions extends SurfaceOptions {
  /** 0..1 where the boundary sits along V. */
  position?: number;
  /** 0..1 how far the boundary wanders. */
  roughness?: number;
  /** 0..1 softness of the transition band. */
  softness?: number;
  /** Bristle streak count reaching past the boundary. */
  bristles?: number;
  /** 0..1 dry-brush holes eaten out of the solid side. */
  dryBrush?: number;
  /** Which side stays opaque. */
  direction?: 'up' | 'down';
}

export function paintedEdgeTexture(o: PaintedEdgeOptions = {}): CanvasTexture {
  const c = common(o);
  const position = o.position ?? 0.5;
  const roughness = o.roughness ?? 0.5;
  const softness = o.softness ?? 0.25;
  const bristles = Math.max(0, Math.round(o.bristles ?? 40));
  const dryBrush = o.dryBrush ?? 0.35;
  const direction = o.direction ?? 'up';

  return bake({
    kind: 'paintedEdge',
    label: `painted-edge:${direction}`,
    key: { ...c.keyBase, position, roughness, softness, bristles, dryBrush, direction },
    size: c.size,
    seed: c.seed,
    // Always linear data: this is a mask, never an albedo.
    data: true,
    wrap: c.wrap,
    repeat: c.repeat,
    anisotropy: c.anisotropy,
    draw: (ctx, size, rand) => {
      ctx.fillStyle = css(HEX.ink);
      ctx.fillRect(0, 0, size, size);

      const edge = tileFbm(rand, 5, 3);
      const band = Math.max(1, softness * size * 0.25);
      const base = position * size;
      const amp = roughness * size * 0.12;
      const steps = 96;
      const boundary = (t: number): number => base + (edge(t, 0.23) - 0.5) * 2 * amp;

      // Solid side, bounded by a wandering brush boundary.
      ctx.beginPath();
      ctx.moveTo(0, direction === 'up' ? size : 0);
      for (let i = 0; i <= steps; i++) ctx.lineTo((i / steps) * size, boundary(i / steps));
      ctx.lineTo(size, direction === 'up' ? size : 0);
      ctx.closePath();
      ctx.fillStyle = css(HEX.white);
      ctx.fill();

      // Feather across the boundary so the transition is paint, not a cut.
      const solidFirst = direction === 'down';
      const feather = ctx.createLinearGradient(0, -band, 0, band);
      feather.addColorStop(0, css(HEX.white, solidFirst ? 1 : 0));
      feather.addColorStop(1, css(HEX.white, solidFirst ? 0 : 1));
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        ctx.save();
        ctx.translate(t * size, boundary(t));
        ctx.fillStyle = feather;
        ctx.fillRect(0, -band, size / steps + 1, band * 2);
        ctx.restore();
      }
      ctx.restore();

      // Bristle streaks poking past the boundary into the empty side.
      ctx.lineCap = 'round';
      for (let i = 0; i < bristles; i++) {
        const t = rand();
        const x = t * size;
        const y = boundary(t);
        const len = size * (0.01 + rand() * 0.07) * (direction === 'up' ? -1 : 1);
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + (rand() - 0.5) * size * 0.02, y + len);
        ctx.strokeStyle = css(HEX.white, 0.35 + rand() * 0.5);
        ctx.lineWidth = Math.max(1, size / 300) * (0.4 + rand());
        ctx.stroke();
      }

      // Dry-brush holes. The canvas is already opaque black-and-white, so these
      // are simply ink ellipses — no compositing tricks, no flatten pass.
      const holes = Math.round(size * 0.35 * dryBrush);
      for (let i = 0; i < holes; i++) {
        const t = rand();
        const x = t * size;
        const y0 = boundary(t);
        const y = direction === 'up' ? y0 + rand() * (size - y0) : rand() * Math.max(1, y0);
        const r = Math.max(1, size / 260) * (0.3 + rand() * 1.4);
        ctx.beginPath();
        ctx.ellipse(x, y, r * 2.2, r, rand() * Math.PI, 0, Math.PI * 2);
        ctx.fillStyle = css(HEX.ink, 0.2 + rand() * 0.5);
        ctx.fill();
      }
    },
  });
}

/* ================================================================== *
 * 9. RECYCLING BADGE — the chasing-arrows mark, codes 1..6
 * ------------------------------------------------------------------
 * Not a tiling surface: a decal for bin fronts, bottle labels and match-board
 * chips. Clamped wrapping, transparent background, thick ink contour so it
 * matches the inverted-hull outlines on the geometry it sits on.
 * ================================================================== */

export interface RecycleBadgeOptions extends SurfaceOptions {
  code?: number;
  abbr?: string;
  tint?: number;
  inkColor?: number;
  /** Fill behind the mark; omit for transparent. */
  background?: number;
}

export function recycleBadgeTexture(o: RecycleBadgeOptions = {}): CanvasTexture {
  const c = common(o, { size: 256, wrap: 'clamp' });
  const code = Math.max(1, Math.min(9, Math.round(o.code ?? 1)));
  const abbr = o.abbr ?? '';
  const tint = o.tint ?? HEX.white;
  const inkColor = o.inkColor ?? HEX.ink;
  const background = o.background ?? -1;

  return bake({
    kind: 'badge',
    label: `badge:${code}${abbr ? '-' + abbr : ''}`,
    key: { ...c.keyBase, code, abbr, tint, inkColor, background },
    size: c.size,
    seed: c.seed,
    data: false,
    wrap: c.wrap,
    repeat: c.repeat,
    anisotropy: c.anisotropy,
    draw: (ctx, size) => {
      ctx.clearRect(0, 0, size, size);
      if (background >= 0) {
        rrect(ctx, size * 0.03, size * 0.03, size * 0.94, size * 0.94, size * 0.1);
        ctx.fillStyle = css(background);
        ctx.fill();
        ctx.strokeStyle = css(inkColor);
        ctx.lineWidth = size * 0.035;
        ctx.stroke();
      }

      const cx = size / 2;
      const cy = size / 2 + size * 0.015;
      const R = size * 0.36;
      const bar = size * 0.085;

      interface Arrow {
        x0: number;
        y0: number;
        hx: number;
        hy: number;
        ux: number;
        uy: number;
      }
      const arrows: Arrow[] = [];
      for (let i = 0; i < 3; i++) {
        const a0 = -Math.PI / 2 + (i * Math.PI * 2) / 3;
        const a1 = -Math.PI / 2 + ((i + 1) * Math.PI * 2) / 3;
        const x0 = cx + Math.cos(a0) * R;
        const y0 = cy + Math.sin(a0) * R;
        const x1 = cx + Math.cos(a1) * R;
        const y1 = cy + Math.sin(a1) * R;
        const len = Math.hypot(x1 - x0, y1 - y0);
        const ux = (x1 - x0) / len;
        const uy = (y1 - y0) / len;
        arrows.push({
          x0: x0 + ux * bar * 0.4,
          y0: y0 + uy * bar * 0.4,
          hx: x1 - ux * bar * 1.5,
          hy: y1 - uy * bar * 1.5,
          ux,
          uy,
        });
      }

      // Ink contour first, fill second — DESIGN.md's "Ink First" rule, applied
      // to a decal instead of a DOM panel.
      for (const pass of [0, 1]) {
        const inkPass = pass === 0;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'butt';
        for (const a of arrows) {
          ctx.beginPath();
          ctx.moveTo(a.x0, a.y0);
          ctx.lineTo(a.hx, a.hy);
          ctx.strokeStyle = inkPass ? css(inkColor) : css(tint);
          ctx.lineWidth = inkPass ? bar + size * 0.028 : bar;
          ctx.stroke();

          const px = -a.uy;
          const py = a.ux;
          ctx.beginPath();
          ctx.moveTo(a.hx + a.ux * bar * 1.7, a.hy + a.uy * bar * 1.7);
          ctx.lineTo(a.hx + px * bar * 1.1, a.hy + py * bar * 1.1);
          ctx.lineTo(a.hx - px * bar * 1.1, a.hy - py * bar * 1.1);
          ctx.closePath();
          if (inkPass) {
            ctx.strokeStyle = css(inkColor);
            ctx.lineWidth = size * 0.056;
            ctx.stroke();
          } else {
            ctx.fillStyle = css(tint);
            ctx.fill();
          }
        }
      }

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineJoin = 'round';
      ctx.font = `900 ${Math.round(size * 0.3)}px system-ui, "Segoe UI", sans-serif`;
      ctx.lineWidth = size * 0.05;
      ctx.strokeStyle = css(inkColor);
      ctx.strokeText(String(code), cx, cy - size * 0.01);
      ctx.fillStyle = css(HEX.white);
      ctx.fillText(String(code), cx, cy - size * 0.01);

      if (abbr) {
        ctx.font = `900 ${Math.round(size * 0.11)}px system-ui, "Segoe UI", sans-serif`;
        ctx.lineWidth = size * 0.03;
        ctx.strokeStyle = css(inkColor);
        ctx.strokeText(abbr, cx, cy + size * 0.28);
        ctx.fillStyle = css(HEX.white);
        ctx.fillText(abbr, cx, cy + size * 0.28);
      }
    },
  });
}

/* ================================================================== *
 * Sample set — one instance of every generator, for the forge demo
 * ================================================================== */

export interface TextureSample {
  label: string;
  note: string;
  texture: CanvasTexture;
}

/**
 * Bake one representative of every generator. Used by `window.__forgeDemo()`
 * and by anyone who wants to eyeball the whole library at once.
 */
export function forgeTextureSamples(): TextureSample[] {
  return [
    {
      label: 'Floor — plank',
      note: 'warm cream, paper tooth, wobbled board seams',
      texture: creamFloorTexture({ pattern: 'plank', cells: 4 }),
    },
    {
      label: 'Floor — tile',
      note: 'kitchen / bathroom variant',
      texture: creamFloorTexture({ pattern: 'tile', cells: 4, seed: 22 }),
    },
    {
      label: 'Floor — height',
      note: 'registered bump channel',
      texture: creamFloorTexture({ pattern: 'plank', cells: 4, channel: 'bump' }),
    },
    {
      label: 'Painted wood',
      note: 'deep-green furniture paint over grain',
      texture: paintedWoodTexture({}),
    },
    {
      label: 'Painted wood — warm',
      note: 'golden-yellow variant for props',
      texture: paintedWoodTexture({ base: mixHex(HEX.goldenYellow, HEX.warmCream, 0.4), seed: 9 }),
    },
    {
      label: 'Fabric — plain weave',
      note: 'soft green sofa upholstery',
      texture: wovenFabricTexture({}),
    },
    {
      label: 'Fabric — twill',
      note: 'diagonal rib, bedding',
      texture: wovenFabricTexture({ weave: 'twill', base: HEX.mintPanel, seed: 5 }),
    },
    { label: 'Ceramic tile', note: 'mint glaze, wobbled grout', texture: ceramicTileTexture({}) },
    {
      label: 'Brushed metal',
      note: 'factory chassis + rails',
      texture: brushedMetalTexture({ rivets: 3 }),
    },
    {
      label: 'Glossy plastic',
      note: 'PET clear-blue tint',
      texture: glossyPlasticTexture({ base: 0x9fd8e8, gloss: 0.75, cloud: 0.15 }),
    },
    {
      label: 'Matte plastic',
      note: 'HDPE waxy, cloudy',
      texture: glossyPlasticTexture({ base: 0xe8e4da, gloss: 0.22, cloud: 0.65, seed: 3 }),
    },
    { label: 'Hatching', note: 'ink shadow texture, 45 deg', texture: hatchOverlayTexture({ style: 'hatch' }) },
    {
      label: 'Crosshatch',
      note: 'deeper shadow band',
      texture: hatchOverlayTexture({ style: 'crosshatch', density: 16, seed: 7 }),
    },
    {
      label: 'Stipple',
      note: 'ink grain for soft occlusion',
      texture: hatchOverlayTexture({ style: 'stipple', density: 26, seed: 11 }),
    },
    { label: 'Painted edge', note: 'brush-drawn silhouette mask', texture: paintedEdgeTexture({}) },
    {
      label: 'Badge 1 PET',
      note: 'chasing arrows decal',
      texture: recycleBadgeTexture({ code: 1, abbr: 'PET', tint: 0x9fd8e8, background: HEX.warmCream }),
    },
    {
      label: 'Badge 5 PP',
      note: 'bin front decal',
      texture: recycleBadgeTexture({ code: 5, abbr: 'PP', tint: 0xf5d9c0, background: HEX.warmCream }),
    },
  ];
}

/* ------------------------------------------------------------------ *
 * Forge demo hook
 * ------------------------------------------------------------------
 * The real demo lives in `geometry.ts` (it needs both halves of the forge).
 * Registering a lazy stub here means `window.__forgeDemo()` works on any route
 * that touched EITHER module. The stub is replaced by the real function the
 * moment `geometry.ts` evaluates.
 * ------------------------------------------------------------------ */
{
  const w = globalThis as unknown as Record<string, unknown>;
  if (typeof window !== 'undefined' && !w.__forgeDemo) {
    w.__forgeDemo = async (): Promise<unknown> => {
      const mod = await import('./geometry');
      return mod.forgeDemo();
    };
  }
}
